import { useContext, useEffect, useState } from 'react';
import {
  AppShell,
  Navbar,
  Aside,
  Text,
  MediaQuery,
  useMantineTheme,
  Title,
  Group,
  Switch,
  Select,
  createStyles,
  Button,
  Paper,
  Center,
  RingProgress,
  SimpleGrid,
  Table,
  ColorScheme,
  Anchor,
  TextInput
} from '@mantine/core';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title as ChartTitle, Tooltip, Legend, Filler, BarElement } from "chart.js";
import { Bar } from 'react-chartjs-2';
import { CircleMarker, LayersControl, MapContainer, Popup, TileLayer } from 'react-leaflet';
import { useLocalStorage, useViewportSize } from '@mantine/hooks';
import { AuthContext } from '../../contexts/Auth/Auth.Context';
import axios from "axios";
import { REACT_APP_UPLOADS } from '../../constants';

const useStyles = createStyles((theme) => ({
    body: {
      display: 'flex',
      alignItems: 'center',
    },
  
    track: {
      width: 40,
      height: 6,
      overflow: 'visible',
    },
  
    thumb: {
      width: 20,
      height: 20,
      left: -2,
      transition: 'background-color 100ms ease, left 100ms ease',
  
      'input:checked + * > &': {
        backgroundColor: theme.fn.primaryColor(),
      },
    },
}));

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ChartTitle,
    Tooltip,
    Filler,
    Legend,
  );
  
  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "top" as const,
      },
  
      title: {
        display: false,
        text: 'WITs',
      },
    },
  };

export default function UserDashboard() {
    const { state, dispatch } = useContext(AuthContext)
    const { classes } = useStyles();
    const theme = useMantineTheme();
    const [opened, setOpened] = useState(false);

      const [colorScheme, setColorScheme] = useLocalStorage<ColorScheme>({
        key: 'color-scheme',
        defaultValue: 'light', // 'light' or 'dark'
        getInitialValueInEffect: true,
      });

    const { height } = useViewportSize();

    const [light, setLight] = useState(true);
    const [basemap, setBasemap] = useState(true);

    const [center, setCenter] = useState<[number, number]>([-0.8, 34.2176]);
    const [zoom, setZoom] = useState(5)

    const [month, setMonth] = useState("all");
    const [year, setYear] = useState("all");
    const [dataset, setDataset] = useState("all");
    const [country, setCountry] = useState("all");
    const [status, setStatus] = useState("all");

    const [data, setData] = useState([]);

    const [offender, setOffender] = useState("");

    const [location, setLocation] = useState("");

    const [allData, setAllData] = useState([]);
    
      useEffect(() => {
          axios.get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/all-cases`, {
                headers: {
                  "ngrok-skip-browser-warning": "true"
                }
              }).then(function(res){
              if(res.status === 200){
                  setData(res.data);
                  setAllData(res.data);
              } else {
                  console.log("Error fetching data", res.data?.message);
              }
          }).catch(function(){
              console.log("An error occured")
          })
      }, [])

      useEffect(() => {
        if(offender.trim() === ""){
          setData(allData);
        } else {
          const filtered = allData.filter((item: any) =>
            item.offender?.toLowerCase() === offender.toLowerCase()
          );
          setData(filtered);
        }
      }, [offender, allData]);

      useEffect(() => {
        if(location.trim() === ""){
          setData(allData);
        } else {
          const filtered = allData.filter((item: any) =>
            item.location?.toLowerCase() === location.toLowerCase()
          );
          setData(filtered);
        }
      }, [WebGLUniformLocation, allData])

      useEffect(() => {
        if (dataset === "all") {
          setData(allData);
        } else {
          // Only include items where the species string includes the selected species
          const filtered = allData.filter((item: any) =>
            item.species?.toLowerCase().split(',').map((s: any) => s.trim()).includes(dataset.toLowerCase())
          );
          setData(filtered);
        }
      }, [dataset, allData]);

      useEffect(() => {
        if (status === "all") {
          setData(allData);
        } else {
          const filtered = allData.filter((item: any) =>
            item.caseStatus?.toLowerCase() === status.toLowerCase()
          );
          setData(filtered);
        }
      }, [status, allData]);

      useEffect(() => {
        if (country === "all" || country === "KE") {
          setData(allData);
        } else {

          setData([]);
        }
      }, [country, allData]);
      

useEffect(() => {
  if (month === "all") {
    setData(allData);
  } else {
    const filtered = allData.filter((item: any) => {
      if (!item.dateOfSeizure) return false;

      const date = new Date(item.dateOfSeizure);
      const itemMonth = date.toLocaleString("default", { month: "long" });

      return itemMonth.toLowerCase() === month.toLowerCase();
    });

    setData(filtered);
  }
}, [month, allData]);


useEffect(() => {
  if (year === "all") {
    setData(allData);
  } else {
    const filtered = allData.filter((item: any) => {
      if (!item.dateOfSeizure) return false;

      const date = new Date(item.dateOfSeizure);
      const itemYear = date.getFullYear();

      return itemYear.toString() === year.toString();
    });

    setData(filtered);
  }
}, [year, allData]);


      function countCaseStatuses() {
        return data.reduce(
          (acc: any, item: any) => {
            const status = item?.caseStatus?.toLowerCase();
      
            if (status === "ongoing") acc.ongoing += 1;
            else if (status === "concluded") acc.concluded += 1;
            else if (status === "absconded") acc.absconded += 1;
      
            return acc;
          },
          { ongoing: 0, concluded: 0, absconded: 0 }
        );
      }

      function getDistinctSpecies() {
        const speciesSet = new Set();
      
        data.forEach((item: any) => {
          if (item?.species && typeof item.species === "string") {
            item.species
              .split(",")
              .map((s: any) => s.trim().toLowerCase()) // normalize casing
              .forEach((s: any) => {
                if (s) speciesSet.add(s);
              });
          }
        });
      
        return Array.from(speciesSet);
      }

      function countObjectsWithSpecies(speciesName: string) {
        const target = speciesName.toLowerCase().trim();
      
        return data.filter((item: any) => {
          if (item.species && typeof item.species === "string") {
            const speciesList = item.species
              .split(",")
              .map((s: any) => s.trim().toLowerCase());
            return speciesList.includes(target);
          }
          return false;
        }).length;
      }
      

      
      function SpeciesChart() {
        const theme = useMantineTheme();
      
        const labels = getDistinctSpecies();
      
        // Create an array of [species, count]
        const speciesCounts = labels.map((species: any) => ({
          species,
          count: countObjectsWithSpecies(species),
        }));
      
        // Sort by count in descending order
        speciesCounts.sort((a, b) => b.count - a.count);
      
        // Take top 4 and group the rest as "Others"
        const top4 = speciesCounts.slice(0, 4);
        const others = speciesCounts.slice(4);
      
        const finalLabels = [...top4.map(item => item.species), 'Others'];
        const finalData = [
          ...top4.map(item => item.count),
          others.reduce((sum, item) => sum + item.count, 0), // Sum of all other species
        ];

        const colors = [
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)',
          'rgba(255, 206, 86, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(153, 102, 255, 0.7)', // for "Others"
        ];
      
        const borderColors = colors.map(color => color.replace('0.7', '1'));
      
        const caseData = {
          labels: finalLabels,
          datasets: [
            {
              label: 'Species Count',
              lineTension: 0.4,
              data: finalData,
              backgroundColor: colors,
              borderColor: borderColors,
              borderWidth: 1
            }
          ],
        };
      
        return (
          <Bar options={options} data={caseData} />
        );
      }
      
      
    
      

    function CaseChart(){
        const theme = useMantineTheme();
    
        const labels = ['KE', 'UG', 'TZ', 'RW', "DRC"];
    
        const caseData = {
            labels,
            datasets: [
              {
                label: 'Absconded',
                lineTension: 0.4,
                data: [countCaseStatuses()?.absconded || 0, 0, 0, 0, 0],
                borderColor: 'rgba(248, 148, 6, 1)',
                backgroundColor: 'rgba(248, 148, 6, 1)',
              },
              {
                label: 'Ongoing',
                lineTension: 0.4,
                data: [countCaseStatuses()?.ongoing || 0, 0, 0, 0, 0],
                borderColor: 'rgba(77, 5, 232, 1)',
                backgroundColor: 'rgba(77, 5, 232, 1)',
              },
              {
                label: 'Concluded',
                lineTension: 0.4,
                data: [countCaseStatuses()?.concluded || 0, 0, 0, 0, 0],
                borderColor: 'rgb(15, 219, 32)',
                backgroundColor: 'rgb(15, 219, 32)',
              }
            ],
          };
        return (
            <Bar options={options} data={caseData} />
        )
      }
  return (
    <AppShell
      styles={{
        main: {
          background: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
        },
      }}
      navbarOffsetBreakpoint="sm"
      asideOffsetBreakpoint="sm"
      navbar={
        basemap ? (
            <Navbar p={0} style={{borderRight: 0}} hiddenBreakpoint="sm" hidden={!opened} width={{ sm: 300, lg: 300 }}>

            <Paper mb={20} withBorder shadow="md" p={"xs"}>
                <Title order={3} mb={20} align='center'>Case Summary</Title>
                <Center mb={20}>
                    <Text>Case Status</Text>
                </Center>
            <SimpleGrid cols={3}>
                <div>
                <RingProgress
                size={80}
                roundCaps
                thickness={4}
                sections={[
                    {
                        value: ((countCaseStatuses().absconded)/data.length) * 100,
                        color: 'rgba(248, 148, 6, 1)'
                    }
                ]}
                label={
                  <Center>
                    <Text>{countCaseStatuses().absconded > 0 ? (((countCaseStatuses().absconded)/data.length) * 100).toFixed(0) : 0}%</Text>
                  </Center>
                }
              />
              <Center>
                <Text size="sm" >Absconded</Text>
                </Center>
                </div>
                <div>
                <RingProgress
                            size={80}
                            roundCaps
                            thickness={4}
                            sections={[
                                {
                                    value: ((countCaseStatuses().ongoing)/data.length) * 100,
                                    color: 'rgba(77, 5, 232, 1)'
                                }
                            ]}
                            label={
                              <Center>
                                <Text>{countCaseStatuses().ongoing > 0 ? (((countCaseStatuses().ongoing)/data.length) * 100).toFixed(0) : 0}%</Text>
                              </Center>
                            }
              />
                <Center>
                <Text  size="sm" >Ongoing</Text>
                </Center>
                </div>
    
                <div>
                <RingProgress
                            size={80}
                            roundCaps
                            thickness={4}
                            sections={[
                                {
                                    value: ((countCaseStatuses().concluded)/data.length) * 100,
                                    color: 'rgb(15, 219, 32)'
                                }
                            ]}
                            label={
                              <Center>
                                <Text>{countCaseStatuses().concluded > 0 ? (((countCaseStatuses().concluded)/data.length) * 100).toFixed(0) : 0}%</Text>
                              </Center>
                            }
              />
                <Center>
                <Text  size="sm" >Concluded</Text>
                </Center>
                </div>
            </SimpleGrid>
            </Paper>
    
            <Paper mb={20} p="xs" withBorder shadow="md">
                <Center mb={20}>
                    <Text>Incident Distribution</Text>
                </Center>
                <CaseChart />
            </Paper>
    
            <Paper mb={20} p="xs" withBorder shadow="md">
                <Center mb={20}>
                    <Text>Species Distribution</Text>
                </Center>
                <SpeciesChart />
            </Paper>
            </Navbar>
        ) : (
            <></>
        )
      }
      aside={
        <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
          <Aside p="md" hiddenBreakpoint="sm" width={{ sm: 300, lg: 350 }}>
            <Group noWrap>
              <img src="/ICON2.png" className="h-14 w-auto" alt="Logo" />
            </Group>
            <Text>
                Welcome to the Wildlife Crime Dashboard. 
                You can explore the data by filtering based on different criteria such as species, case status, and geographical location.
            </Text>
            <Group p="md">
                <Switch checked={basemap} onChange={(e) =>{setBasemap(basemap ? false : true)}} label={"Summary"} classNames={classes} />
              <Switch checked={colorScheme === "dark"} onChange={(e) =>{setColorScheme(colorScheme === "dark" ? "light" : "dark")}} label={"Dark Theme"} classNames={classes} />
            </Group>
            <Select label="Country" data={[
                {label: "All", value:"all"},
                {label: "Kenya", value:"KE"},
                {label: "Uganda", value:"UG"},
                {label: "Tanzania", value:"TZ"},
                {label: "Rwanda", value:"RW"},
                {label: "DRC", value:"DRC"}
            ]} value={country} onChange={(val: string) => {setCountry(val)}} mb="md" />
                <Select searchable label="Species" mb="md" value={dataset} onChange={(val: string) => {setDataset(val)}} data={[
                    {label: "Elephants", value:"elephant"},
                    {label: "Dik-Dik", value: "dik-dik"},
                    {label: "Sandalwood", value: "sandalwood"},
                    {label: "Zebra", value: "zebra"},
                    {label: "All", value: "all"}
                ]} />

                <Select searchable label="Case Status" mb="md" value={status} onChange={(val: string) => {setStatus(val)}} data={[
                    {label: "Concluded", value:"Concluded"},
                    {label: "Ongoing", value: "Ongoing"},
                    {label: "Absconded", value: "Absconded"},
                    {label: "Show All", value: "all"}
                ]} />
                <Select mb="md" label='Month of Seizure' value={month} onChange={(val: string) => {setMonth(val)}} data={[
                    {label: "All", value:"all"},
                    {label: "January", value:"January"},
                    {label: "February", value:"February"},
                    {label: "March", value:"March"},
                    {label: "April", value:"April"},
                    {label: "May", value:"May"},
                    {label: "June", value:"June"},
                    {label: "July", value:"July"},
                    {label: "August", value:"August"},
                    {label: "September", value:"September"},
                    {label: "October", value:"October"},
                    {label: "November", value:"November"},
                    {label: "December", value:"December"}
                ]} />
                <Select mb="md" label='Year of Seizure' value={year} onChange={(val: string) => {setYear(val)}} data={[
                    {label: "All", value:"all"},
                    {label: "2010", value:"2010"},
                    {label: "2011", value:"2011"},
                    {label: "2012", value:"2012"},
                    {label: "2013", value:"2013"},
                    {label: "2014", value:"2014"},
                    {label: "2015", value:"2015"},
                    {label: "2016", value:"2016"},
                    {label: "2017", value:"2017"},
                    {label: "2018", value:"2018"},
                    {label: "2019", value:"2019"},
                    {label: "2020", value:"2020"},
                    {label: "2021", value:"2021"},
                    {label: "2022", value:"2022"},
                    {label: "2023", value:"2023"},
                    {label: "2024", value:"2024"},
                    {label: "2025", value:"2025"},
                ]} />

                <Group mt="md">
                {state.userData?.role === "admin" && (
                        <Anchor underline size={"xs"} variant="text" href='/home/overview'>Admin Console</Anchor>

                )}
                <Anchor onClick={() => {dispatch({type: "SIGN_OUT"})}} underline size={"xs"} variant="text" href='#'>Sign Out</Anchor>
                </Group>
          </Aside>
        </MediaQuery>
      }
    >
                   <MapContainer style={{width: '100%', height: "100%", backgroundColor: light ? "white" : "black"}} center={center} zoom={zoom} scrollWheelZoom={true}>
                   <LayersControl>
              <LayersControl.BaseLayer checked={true} name='OSM'>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url= { !light ? "https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png" : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
              />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name='Satellite'>
              <TileLayer
                attribution='&copy; Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
              </LayersControl.BaseLayer>
            </LayersControl>

                  {data.map((itm:any, idx: number) => {
                    return itm?.latitude && itm?.longitude ? (
                      <CircleMarker
                        key={idx}
                        center={[itm.latitude, itm.longitude]}
                        radius={8} // Customize the radius of the circle
                        color="white" // Customize the border color
                        fillColor={itm.caseStatus.toLowerCase() === "ongoing" ? "rgba(77, 5, 232, 1)" : itm.caseStatus.toLowerCase() === "concluded" ? "rgb(15, 219, 32)" : "rgba(248, 148, 6, 1)"} // Customize the fill color
                        fillOpacity={1} // Customize the fill opacity
                      >
                        <Popup>
                          <Title order={3}>Species Involved:</Title>
                          <Text>{itm.species}</Text>
            
                          <br />
                          <Title order={3}>Location:</Title>
                          <Text>{itm?.location}</Text>
                          <br />
            
                          <Title order={3}>Name of Accused:</Title>
                          <Text>{itm?.nameOfAccused}</Text>
                          <br />
                          <Anchor href="#">View More Details</Anchor>
                        </Popup>
                      </CircleMarker>
                    ) : null;
                  })}
                   </MapContainer>
    </AppShell>
  );
}